import { Fragment } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';

export interface Props {
  open: boolean;
  stay: () => void;
  leave: () => void;
}

/** The confirm behind useLeaveTrailGuard: shown when the player presses the
 * phone's back button on the trail map. Dismissing it any way other than the
 * Leave button keeps them on the trail. */
function LeaveTrailDialog({ open, stay, leave }: Props) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={stay}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="bg-mt-surface relative w-full transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-center shadow-xl transition-all sm:my-8 sm:max-w-sm sm:p-6">
                <DialogTitle
                  as="h3"
                  className="text-mt-ink text-base leading-6 font-semibold"
                >
                  Leave the trail?
                </DialogTitle>
                <p className="text-mt-ink-muted mt-2 text-sm">
                  Your progress is saved on this phone, so you can pick up where
                  you left off.
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <button
                    type="button"
                    className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs"
                    onClick={stay}
                  >
                    Keep playing
                  </button>
                  <button
                    type="button"
                    className="bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset"
                    onClick={leave}
                  >
                    Leave
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default LeaveTrailDialog;
